import { useState } from "react";
import type { CycleResponse } from "../../api/client";
import { useCycleDetail } from "../../hooks/useCycleDetail";
import { cn } from "@/lib/utils";
import PnlValue from "../PnlValue";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/states";
import PositionLifecycleTable from "../dashboard/PositionLifecycleTable";
import CycleTimeline from "../dashboard/CycleTimeline";

interface CycleDetailDrawerProps {
  cycle: CycleResponse;
}

type Tab = "positions" | "stops" | "timeline";

/**
 * Drawer forensic d'un cycle (page Historique) : positions, mouvements de stop,
 * timeline. L'ex-onglet « Overview » dupliquait 8 cellules de la ligne parente —
 * remplacé par une bande d'en-tête (durée, ouvertes/fermées) ; la paire « P&L
 * ligne vs Σ positions » ne s'affiche QUE quand elle diverge (c'est un signal de
 * réconciliation, pas une donnée). Les 2 onglets placeholder (« pas encore
 * exposé ») sont supprimés — ils reviendront le jour où l'endpoint existera.
 */
export default function CycleDetailDrawer({ cycle }: CycleDetailDrawerProps) {
  const [tab, setTab] = useState<Tab>("positions");
  const detail = useCycleDetail(cycle.id);

  const positions = detail.positions ?? [];
  const closed = positions.filter((p) => p.closedAt != null);
  const open = positions.filter((p) => p.closedAt == null);
  const synthetic = closed.some(
    (p) => p.closeSource != null && p.closeSource.startsWith("RECONCILE_"),
  );
  const closedKnown = closed.filter(
    (p) => p.realizedPnlEur != null && Number.isFinite(Number(p.realizedPnlEur)),
  );
  const pnlSumFromPositions =
    closed.length === 0
      ? null
      : closedKnown.reduce((acc, p) => acc + Number(p.realizedPnlEur), 0);

  // Signal de réconciliation : le P&L de la ligne cycle et la somme des positions
  // divergent (ou l'un des deux manque alors que l'autre existe). Évalué SEULEMENT
  // une fois le détail chargé et le cycle terminé — jamais d'alarme transitoire.
  const rowPnl =
    cycle.realizedPnlEur == null || !Number.isFinite(Number(cycle.realizedPnlEur))
      ? null
      : Number(cycle.realizedPnlEur);
  const diverges =
    cycle.endedAt != null &&
    detail.positions != null &&
    ((rowPnl != null &&
      pnlSumFromPositions != null &&
      Math.abs(rowPnl - pnlSumFromPositions) >= 0.01) ||
      (rowPnl == null) !== (pnlSumFromPositions == null));

  return (
    <div
      className="cycle-detail bg-card border-border flex flex-col gap-3 rounded-lg border p-3"
      data-testid={`cycle-detail-${cycle.id}`}
    >
      {/* bande d'en-tête : ce que la ligne parente ne porte pas déjà */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs tabular-nums">
        <span>
          durée <span className="text-foreground font-mono">{formatDuration(cycle.startedAt, cycle.endedAt)}</span>
        </span>
        <span data-testid={`drawer-counts-${cycle.id}`}>
          ouvertes / fermées{" "}
          <span className="text-foreground font-mono">
            {open.length} / {closed.length}
          </span>
        </span>
      </div>

      {diverges && (
        <div
          className="border-warning/40 bg-warning-soft text-warning flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-2.5 py-1.5 text-xs"
          role="status"
          data-testid={`drawer-pnl-divergence-${cycle.id}`}
        >
          <span className="font-semibold">⚠ P&amp;L divergent (signal de réconciliation)</span>
          <span>
            ligne cycle{" "}
            <PnlValue
              value={cycle.realizedPnlEur}
              pnlComplete={cycle.pnlComplete}
              synthetic={synthetic}
              testId={`drawer-cycle-pnl-${cycle.id}`}
            />
          </span>
          <span>
            Σ positions{" "}
            <PnlValue
              value={pnlSumFromPositions}
              pnlComplete={closed.length === 0 ? null : closedKnown.length === closed.length}
              synthetic={synthetic}
              testId={`drawer-positions-pnl-${cycle.id}`}
            />
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-1" role="tablist">
        <TabBtn label={`Positions (${positions.length})`} active={tab === "positions"} onClick={() => setTab("positions")} />
        <TabBtn label={`Stops (${detail.stopMovements?.length ?? 0})`} active={tab === "stops"} onClick={() => setTab("stops")} />
        <TabBtn label="Timeline" active={tab === "timeline"} onClick={() => setTab("timeline")} />
      </div>

      <div role="tabpanel">
        {detail.error && (
          <p className="text-loss text-sm" role="alert" data-testid="drawer-error">
            Échec du chargement du détail — cette vue n'est pas l'état live.
          </p>
        )}

        {tab === "positions" && (
          <PositionLifecycleTable
            positions={detail.positions}
            loading={detail.loading}
            error={detail.error}
          />
        )}

        {tab === "stops" && (
          <StopMovementsTable
            movements={detail.stopMovements}
            loading={detail.loading}
            error={detail.error}
          />
        )}

        {tab === "timeline" && (
          <CycleTimeline
            cycle={cycle}
            positions={detail.positions}
            stopMovements={detail.stopMovements}
          />
        )}
      </div>
    </div>
  );
}

function StopMovementsTable({
  movements,
  loading,
  error,
}: {
  movements: import("../../api/client").StopMovementResponse[] | null;
  loading: boolean;
  error: boolean;
}) {
  if (error) {
    return <ErrorState title="Échec du chargement des mouvements de stop." />;
  }
  if (loading && movements == null) {
    return <LoadingState lines={2} />;
  }
  if (movements == null || movements.length === 0) {
    return <EmptyState>Aucun mouvement de stop pour ce cycle.</EmptyState>;
  }
  return (
    <div className="overflow-x-auto">
      <table
        className="lifecycle-table w-full text-sm whitespace-nowrap"
        data-testid="drawer-stops-table"
      >
        <thead>
          <tr className="text-muted-foreground border-border border-b text-left text-[0.68rem] tracking-wide uppercase">
            <th className="px-2 py-2 font-medium">État</th>
            <th className="px-2 py-2 font-medium">Position</th>
            <th className="px-2 py-2 font-medium">Type</th>
            <th className="px-2 py-2 font-medium">Ancien SL</th>
            <th className="px-2 py-2 font-medium">Nouveau SL</th>
            <th className="px-2 py-2 font-medium">Proposé</th>
            <th className="px-2 py-2 font-medium">Raison</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((s) => (
            <tr key={s.id} className="border-border/60 border-b font-mono tabular-nums">
              <td className="px-2 py-1.5">
                <span
                  className={cn(
                    "rounded border px-1.5 py-0.5 text-[0.65rem] font-semibold",
                    s.accepted
                      ? "bg-buy-soft text-buy border-buy/30"
                      : "bg-sell-soft text-sell border-sell/30",
                  )}
                >
                  {s.accepted ? "ACCEPTÉ" : "REFUSÉ"}
                </span>
              </td>
              <td className="px-2 py-1.5">{s.positionId}</td>
              <td className="px-2 py-1.5 font-sans">{s.kind}</td>
              <td className="px-2 py-1.5">{s.oldStop == null ? "—" : Number(s.oldStop).toFixed(2)}</td>
              <td className="px-2 py-1.5">{Number(s.newStop).toFixed(2)}</td>
              <td className="text-muted-foreground px-2 py-1.5">{formatTime(s.proposedAt)}</td>
              <td className="text-muted-foreground px-2 py-1.5 font-sans">{s.skipReason ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-overlay text-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDuration(startIso: string, endIso: string | null): string {
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return "—";
  const end = endIso ? Date.parse(endIso) : Date.now();
  if (Number.isNaN(end)) return "—";
  const secs = Math.max(0, Math.floor((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}
